import React, { useState, useEffect, useMemo } from 'react';
import {
  FileSignature,
  Users,
  ClipboardList,
  Megaphone,
  Vote,
  Building2,
  ChevronRight,
  Calendar,
  MapPin,
  Activity,
  Lock,
  CheckCircle,
  Clock,
  AlertCircle,
  LogOut,
  Shield,
  User,
  BarChart
} from 'lucide-react';
import Login from './Login';
import ChangePasswordModal from './ChangePasswordModal';
import { authService, User as AuthUser, UserRole } from './authService';
import { getApiUrl, getRegionPrefix, getCurrentRegion, RegionInfo } from './utils/apiConfig';
import './App.css';

// Mapping des titres par région
const REGION_TITLES: Record<string, { title: string; region: string }> = {
  BOUAKE: { title: 'Conquête Bouaké 2025', region: 'Région du Gbêkê' },
  ALEPE: { title: 'Conquête Alépé 2025', region: 'Région de La Mé' },
  BOUNDIALI: { title: 'Conquête Boundiali 2025', region: 'Région de la Bagoué' },
  ABOBO: { title: 'Conquête Abobo 2025', region: 'Commune d\'Abobo' }
};

// Helper pour obtenir le titre selon la région
const getRegionTitle = (): { title: string; region: string } => {
  const currentRegion = getCurrentRegion();
  if (currentRegion && REGION_TITLES[currentRegion.code]) {
    return REGION_TITLES[currentRegion.code];
  }
  return { title: 'Conquête 2025', region: 'Côte d\'Ivoire' };
};

interface Module {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  url: string;
  status: 'active' | 'coming-soon' | 'locked';
  color: string;
  bgGradient: string;
  stats?: {
    label: string;
    value: string;
  };
}

interface BureauInfo {
  membre_bureau_id: number;
  fonction: string;
  niveau: string;
  nom_niveau: string;
}

const App: React.FC = () => {
  const [selectedModule, setSelectedModule] = useState<Module | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date()); // Horloge
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userBureau, setUserBureau] = useState<BureauInfo | null>(null); // 🆕 État pour le bureau
  const [showPasswordModal, setShowPasswordModal] = useState(false); // 🆕 Modal changement mdp
  const [stats, setStats] = useState({
    totalElecteurs: 0,
    electeursIdentifies: 0,
    electeursAvecTelephone: 0,
    totalModules: 6,
    totalUtilisateurs: 0,
    utilisateursActifs: 0,
    utilisateursInactifs: 0,
    objectifParrainages: 0,
    bureauxVote: 0,
    region: 'DEFAULT'
  });

  // 🆕 Fonction pour vérifier si l'utilisateur est membre d'un bureau
  const checkBureauMembership = async (): Promise<void> => {
    try {
      const token = authService.getToken();
      if (!token) {
        return;
      }

      const response = await fetch(getApiUrl('/api/bureaux/dashboard/mon-bureau'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setUserBureau(data.data);
        }
      }
    } catch (error) {
      console.error('Erreur lors de la vérification du bureau:', error);
    }
  };

  useEffect(() => {
    // Forcer le rechargement depuis localStorage au montage
    authService.loadFromStorage();

    // Vérifier l'authentification au chargement de la page
    const user = authService.getCurrentUser();
    if (user && authService.isAuthenticated()) {
      setCurrentUser(user);

      // Rediriger selon le rôle si nécessaire
      if (!authService.shouldShowPortal()) {
        window.location.href = authService.getRedirectionUrl();
      }

      // Charger les statistiques d'abord puis vérifier le bureau
      fetchStatistics(user).then(() => {
        checkBureauMembership();
      }).catch(() => {
        checkBureauMembership();
      });
    } else {
      // Sans user, charger les stats par défaut
      setStats({
        totalElecteurs: 461047,
        electeursIdentifies: 72384,
        electeursAvecTelephone: 72384,
        totalModules: 6,
        totalUtilisateurs: 527,
        utilisateursActifs: 383,
        utilisateursInactifs: 144,
        objectifParrainages: 40000,
        bureauxVote: 0,
        region: 'BOUAKE'
      });
    }
    setIsLoading(false);
  }, []);

  const fetchStatistics = async (user: AuthUser) => {
    try {
      const token = authService.getToken();

      // ✅ Vérifier que le token existe avant de faire l'appel
      if (!token) {
        console.warn('⚠️ Pas de token, utilisation des valeurs par défaut');
        setStats({
          totalElecteurs: 461047,
          electeursIdentifies: 72384,
          electeursAvecTelephone: 72384,
          totalModules: 6,
          totalUtilisateurs: 527,
          utilisateursActifs: 383,
          utilisateursInactifs: 144,
          objectifParrainages: 40000,
          bureauxVote: 0,
          region: 'BOUAKE'
        });
        return;
      }

      // Use relative URL to go through Gateway (with region prefix)
      const response = await fetch(getApiUrl('/api/stats'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        }
      });

      // ✅ Gérer les erreurs 429 (Too Many Requests) silencieusement
      if (response.status === 429) {
        console.warn('⚠️ Rate limit atteint, utilisation des valeurs en cache');
        // Ne pas faire d'autre appel, utiliser les valeurs par défaut
        setStats({
          totalElecteurs: 461047,
          electeursIdentifies: 72384,
          electeursAvecTelephone: 72384,
          totalModules: 6,
          totalUtilisateurs: 527,
          utilisateursActifs: 383,
          utilisateursInactifs: 144,
          objectifParrainages: 40000,
          bureauxVote: 0,
          region: 'BOUAKE'
        });
        return;
      }

      // ✅ Gérer les erreurs 401 sans déconnecter (déjà géré par authService)
      if (response.status === 401) {
        console.warn('⚠️ Erreur 401 sur /api/stats, utilisation des valeurs par défaut');
        setStats({
          totalElecteurs: 461047,
          electeursIdentifies: 72384,
          electeursAvecTelephone: 72384,
          totalModules: 6,
          totalUtilisateurs: 527,
          utilisateursActifs: 383,
          utilisateursInactifs: 144,
          objectifParrainages: 40000,
          bureauxVote: 0,
          region: 'BOUAKE'
        });
        return;
      }

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Récupérer aussi les stats des modules pour objectif parrainages et bureaux de vote
          let objectifParrainages = 0;
          let bureauxVote = 0;
          try {
            const modulesResponse = await fetch(getApiUrl('/api/stats/modules'), {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Cache-Control': 'no-cache'
              }
            });
            if (modulesResponse.ok) {
              const modulesData = await modulesResponse.json();
              if (modulesData.success && modulesData.modules) {
                const parrainagesModule = modulesData.modules.find((m: any) => m.id === 'parrainages');
                const presidentielles = modulesData.modules.find((m: any) => m.id === 'presidentielles');
                objectifParrainages = parrainagesModule?.stats?.objectif || 0;
                bureauxVote = presidentielles?.stats?.bureauxVote || 0;
              }
            }
          } catch (e) {
            console.log('Erreur stats modules:', e);
          }

          setStats({
            totalElecteurs: data.stats.totalElecteurs,
            electeursIdentifies: data.stats.totalIdentifies,
            electeursAvecTelephone: data.stats.totalIdentifies,
            totalModules: data.stats.totalModules,
            totalUtilisateurs: data.stats.totalUtilisateurs,
            utilisateursActifs: data.stats.utilisateursActifs || data.stats.totalUtilisateurs,
            utilisateursInactifs: data.stats.utilisateursInactifs || 0,
            objectifParrainages: objectifParrainages,
            bureauxVote: bureauxVote,
            region: data.stats.region || 'DEFAULT'
          });
        }
      } else {
        // Si l'API échoue, utiliser les valeurs par défaut
        console.warn(`⚠️ Erreur ${response.status} sur /api/stats, utilisation des valeurs par défaut`);
        setStats({
          totalElecteurs: 461047,
          electeursIdentifies: 72384,
          electeursAvecTelephone: 72384,
          totalModules: 6,
          totalUtilisateurs: 527,
          utilisateursActifs: 383,
          utilisateursInactifs: 144,
          objectifParrainages: 40000,
          bureauxVote: 0,
          region: 'BOUAKE'
        });
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des statistiques:', error);
      // Valeurs par défaut en cas d'erreur
      setStats({
        totalElecteurs: 461047,
        electeursIdentifies: 72384,
        electeursAvecTelephone: 72384,
        totalModules: 6,
        totalUtilisateurs: 527,
        utilisateursActifs: 383,
        utilisateursInactifs: 144,
        objectifParrainages: 40000,
        bureauxVote: 0,
        region: 'BOUAKE'
      });
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Mettre à jour le titre de l'onglet selon la région
  useEffect(() => {
    const regionInfo = getRegionTitle();
    document.title = regionInfo.title;
  }, []);

  // ❌ SUPPRIMÉ: Ce useEffect causait une boucle infinie de polling
  // Les stats sont déjà chargées dans le useEffect initial (ligne 68)
  // Pas besoin de recharger à chaque changement de currentUser
  // useEffect(() => {
  //   if (currentUser) {
  //     fetchStatistics(currentUser);
  //   }
  // }, [currentUser]);

  // 🆕 Modules de base (toujours visibles) - Memoïsés pour éviter les recalculs
  const baseModules: Module[] = useMemo(() => [
    {
      id: 'identification',
      name: 'Identification',
      description: 'Collecte et mise à jour des informations des électeurs',
      icon: <Users className="w-8 h-8" />,
      url: '/identification',
      status: 'active',
      color: 'text-blue-600',
      bgGradient: 'from-blue-400 to-blue-600',
      stats: {
        label: 'Statistiques',
        value: `Électeurs: ${new Intl.NumberFormat('fr-FR').format(stats.totalElecteurs)} | Identifiés: ${new Intl.NumberFormat('fr-FR').format(stats.electeursIdentifies)} (${((stats.electeursIdentifies / stats.totalElecteurs) * 100).toFixed(1)}%)`
      }
    },
    {
      id: 'rle',
      name: 'RLE',
      description: 'Révision de la Liste Électorale - Mise à jour et corrections',
      icon: <ClipboardList className="w-8 h-8" />,
      url: '/rle',
      status: 'coming-soon',
      color: 'text-green-600',
      bgGradient: 'from-green-400 to-green-600',
      stats: {
        label: 'Période',
        value: '2026'
      }
    },
    {
      id: 'parrainages',
      name: 'Parrainages',
      description: 'Gestion des parrainages électoraux avec système de registres physiques et CRM',
      icon: <FileSignature className="w-8 h-8" />,
      url: '/parrainages',
      status: 'locked',
      color: 'text-orange-600',
      bgGradient: 'from-orange-400 to-orange-600',
      stats: {
        label: 'Objectif',
        value: `${new Intl.NumberFormat('fr-FR').format(stats.objectifParrainages)} parrainages`
      }
    },
    {
      id: 'mobilisation',
      name: 'Mobilisation',
      description: 'Campagnes de communication SMS, WhatsApp et appels',
      icon: <Megaphone className="w-8 h-8" />,
      url: '/mobilisation',
      status: 'active',
      color: 'text-purple-600',
      bgGradient: 'from-purple-400 to-purple-600',
      stats: {
        label: 'Contacts',
        value: new Intl.NumberFormat('fr-FR').format(stats.electeursIdentifies)
      }
    },
    {
      id: 'presidentielles',
      name: 'Présidentielles 2025',
      description: 'Suivi participation temps réel et saisie résultats électoraux',
      icon: <Vote className="w-8 h-8" />,
      url: '/presidentielles',
      status: 'active',
      color: 'text-red-600',
      bgGradient: 'from-red-400 to-red-600',
      stats: {
        label: 'Bureaux de vote',
        value: `${new Intl.NumberFormat('fr-FR').format(stats.bureauxVote)} bureaux`
      }
    },
    {
      id: 'legislatives',
      name: 'Législatives 2025',
      description: 'Gestion des candidatures et résultats des élections législatives',
      icon: <Building2 className="w-8 h-8" />,
      url: '/legislatives',
      status: 'locked',
      color: 'text-indigo-600',
      bgGradient: 'from-indigo-400 to-indigo-600',
      stats: {
        label: 'Date',
        value: 'Décembre 2025'
      }
    },
    {
      id: 'admin-configuration',
      name: 'Admin Configuration',
      description: 'Configuration des sections politiques, affectations GEP et bureaux hiérarchiques',
      icon: <Building2 className="w-8 h-8" />,
      url: '/admin',
      status: 'active',
      color: 'text-orange-600',
      bgGradient: 'from-orange-500 to-orange-700',
      stats: {
        label: 'Accès',
        value: 'Administrateurs uniquement'
      }
    }
  ], [stats]);

  // 🆕 Fusionner les modules de base avec le module Bureaux si l'utilisateur est membre
  const modules = useMemo(() => {
    // Créer le module "Mes Statistiques" si l'utilisateur est membre d'un bureau
    const bureauxModule: Module | null = userBureau ? {
      id: 'bureaux',
      name: 'Mes Statistiques',
      description: `Dashboard ${userBureau.niveau} - ${userBureau.fonction}`,
      icon: <BarChart className="w-8 h-8" />,
      url: '/bureaux',
      status: 'active',
      color: 'text-purple-600',
      bgGradient: 'from-purple-500 to-purple-700',
      stats: {
        label: userBureau.nom_niveau,
        value: userBureau.fonction
      }
    } : null;

    // 🆕 VIP: Ne montrer que la carte "Mes Statistiques"
    if (currentUser?.role === 'VIP') {
      return bureauxModule ? [bureauxModule] : [];
    }

    // Pour les autres rôles, filtrer admin-configuration si non ADMINISTRATEUR
    let allModules = [...baseModules];
    if (currentUser?.role !== 'ADMINISTRATEUR') {
      allModules = allModules.filter(m => m.id !== 'admin-configuration');
    }

    // Ajouter la carte "Mes Statistiques" EN PREMIER si disponible
    if (bureauxModule) {
      allModules.unshift(bureauxModule);
    }

    return allModules;
  }, [userBureau, baseModules, currentUser]);

  const handleLogin = async (username: string, password: string) => {
    const user = await authService.login(username, password);
    setCurrentUser(user);

    // Charger les stats filtrées par coordination
    await fetchStatistics(user);

    // Vérifier le bureau de l'utilisateur
    await checkBureauMembership();

    // Vérifier si changement de mot de passe requis
    if (authService.requirePasswordChange) {
      setShowPasswordModal(true);
      return; // Ne pas rediriger tant que le mdp n'est pas changé
    }

    // Rediriger selon le rôle SEULEMENT si l'utilisateur ne doit pas voir le portail
    // Les utilisateurs autorisés (ADMINISTRATEUR, VIP, DELEGUE_*, etc.) restent sur le portail
    if (!authService.shouldShowPortal()) {
      const redirectUrl = authService.getRedirectionUrl();
      window.location.href = redirectUrl;
    }
    // Sinon, l'utilisateur reste sur le portail (pas de redirection)
  };

  const handlePasswordChangeSuccess = () => {
    setShowPasswordModal(false);
    authService.setRequirePasswordChange(false);
    // Recharger la page pour continuer normalement
    window.location.reload();
  };

  const handleLogout = () => {
    authService.logout();
    setCurrentUser(null);
  };

  const handleModuleClick = (module: Module) => {
    console.log('🔍 [Portail] handleModuleClick called for module:', module.id, 'status:', module.status);

    if (module.status === 'active') {
      // Vérifier les permissions de l'utilisateur
      let hasAccess = false;

      switch (module.id) {
        case 'identification':
          hasAccess = authService.hasPermission('identification.view');
          break;
        case 'parrainages':
          hasAccess = authService.hasPermission('parrainages.view');
          break;
        case 'mobilisation':
          hasAccess = authService.hasPermission('mobilisation.view');
          break;
        case 'rle':
          hasAccess = authService.hasPermission('rle.view');
          break;
        case 'presidentielles':
          hasAccess = authService.hasPermission('presidentielles.view');
          break;
        case 'legislatives':
          hasAccess = authService.hasPermission('legislatives.view');
          break;
        case 'admin-configuration':
          hasAccess = currentUser?.role === 'ADMINISTRATEUR';
          break;
        case 'bureaux':
          // VIP et ADMIN ont accès même sans bureau, sinon il faut être membre d'un bureau
          hasAccess = !!userBureau || currentUser?.role === 'ADMINISTRATEUR' || currentUser?.role === 'VIP';
          break;
        default:
          hasAccess = false;
      }

      if (!hasAccess) {
        console.log('❌ [Portail] Access denied for module:', module.id);
        setSelectedModule({ ...module, status: 'locked' });
        setTimeout(() => setSelectedModule(null), 3000);
        return;
      }

      // Utiliser le préfixe de région actuel pour maintenir le contexte
      const regionPrefix = getRegionPrefix();

      let targetPath = '';
      if (module.id === 'parrainages') {
        targetPath = `${regionPrefix}/parrainages`;
      } else if (module.id === 'identification') {
        targetPath = `${regionPrefix}/identification`;
      } else if (module.id === 'mobilisation') {
        targetPath = `${regionPrefix}/mobilisation`;
      } else if (module.id === 'admin-configuration') {
        targetPath = `${regionPrefix}/admin`;
      } else if (module.id === 'bureaux') {
        targetPath = `${regionPrefix}/bureaux`;
      } else if (module.id === 'presidentielles') {
        targetPath = `${regionPrefix}/presidentielle`;
      } else {
        targetPath = `${regionPrefix}${module.url}`;
      }

      console.log('✅ [Portail] Navigating to:', targetPath);
      // Force full page reload to navigate to another module
      window.location.replace(targetPath);
    } else {
      setSelectedModule(module);
      setTimeout(() => setSelectedModule(null), 3000);
    }
  };

  const getStatusBadge = (status: Module['status']) => {
    switch (status) {
      case 'active':
        return (
          <span className="flex items-center text-green-600 text-sm font-medium">
            <CheckCircle className="w-4 h-4 mr-1" />
            Actif
          </span>
        );
      case 'coming-soon':
        return (
          <span className="flex items-center text-yellow-600 text-sm font-medium">
            <Clock className="w-4 h-4 mr-1" />
            Bientôt
          </span>
        );
      case 'locked':
        return (
          <span className="flex items-center text-gray-500 text-sm font-medium">
            <Lock className="w-4 h-4 mr-1" />
            Verrouillé
          </span>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <Activity className="w-12 h-12 text-orange-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  // Si pas connecté, afficher la page de login
  if (!currentUser) {
    return <Login onLogin={handleLogin} />;
  }

  // Si l'utilisateur ne doit pas voir le portail, rediriger
  if (!authService.shouldShowPortal()) {
    window.location.href = authService.getRedirectionUrl();
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                  <Activity className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <h1 className="text-2xl font-bold text-gray-900">{getRegionTitle().title}</h1>
                <p className="text-sm text-gray-500">Plateforme de Gestion du Processus Électoral en Côte d'Ivoire</p>
              </div>
            </div>
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2 bg-gray-50 px-4 py-2 rounded-lg">
                {currentUser.role === UserRole.ADMINISTRATEUR && (
                  <Shield className="w-5 h-5 text-orange-600" />
                )}
                {currentUser.role === UserRole.VIP && (
                  <Shield className="w-5 h-5 text-purple-600" />
                )}
                <User className="w-5 h-5 text-gray-600" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{currentUser.nom_complet}</p>
                  <p className="text-xs text-gray-500">{currentUser.role}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Déconnexion</span>
              </button>
              <div className="text-right">
                <p className="text-sm text-gray-500">
                  <MapPin className="inline w-4 h-4 mr-1" />
                  {getRegionTitle().region}
                </p>
                <p className="text-xs text-gray-400">
                  <Calendar className="inline w-3 h-3 mr-1" />
                  {currentTime.toLocaleDateString('fr-FR', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h2 className="text-4xl font-bold mb-4">Bienvenue {currentUser.nom_complet}</h2>
            <p className="text-xl opacity-90">
              {currentUser.role === UserRole.VIP ?
                'Accédez à vos statistiques personnalisées' :
                'Sélectionnez un module pour commencer'
              }
            </p>
            <div className="mt-6 flex justify-center space-x-8">
              <div className="text-center">
                <p className="text-3xl font-bold">{new Intl.NumberFormat('fr-FR').format(stats.totalElecteurs)}</p>
                <p className="text-sm opacity-75">Électeurs</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold">{stats.totalModules}</p>
                <p className="text-sm opacity-75">Modules</p>
              </div>
              {(currentUser.role === UserRole.ADMINISTRATEUR || currentUser.role === UserRole.VIP) && (
                <div className="text-center">
                  <p className="text-3xl font-bold">{new Intl.NumberFormat('fr-FR').format(stats.totalUtilisateurs)}</p>
                  <p className="text-sm opacity-75">Utilisateurs</p>
                  <div className="flex justify-center gap-4 mt-2 text-xs">
                    <span className="text-green-400">✓ {stats.utilisateursActifs} actifs</span>
                    <span className="text-gray-400">✗ {stats.utilisateursInactifs} inactifs</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modules Grid - Responsive: horizontal on large screens, vertical on mobile */}
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-6 justify-center">
          {modules.map((module) => (
            <div
              key={module.id}
              onClick={() => handleModuleClick(module)}
              className={`
                relative bg-white rounded-xl shadow-lg overflow-hidden
                transform transition-all duration-300 hover:scale-105
                w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.33%-16px)] xl:w-[calc(25%-18px)] 2xl:w-[calc(20%-19px)]
                min-w-[280px] max-w-[400px]
                ${module.status === 'active'
                  ? 'cursor-pointer hover:shadow-2xl'
                  : 'cursor-not-allowed opacity-75'}
              `}
            >
              {/* Module Header with Gradient */}
              <div className={`bg-gradient-to-r ${module.bgGradient} p-6 text-white`}>
                <div className="flex justify-between items-start">
                  <div className="p-3 bg-white/20 backdrop-blur rounded-lg inline-block">
                    {module.icon}
                  </div>
                  {getStatusBadge(module.status)}
                </div>
                <h3 className="text-2xl font-bold mt-4">{module.name}</h3>
              </div>

              {/* Module Body */}
              <div className="p-6">
                <p className="text-gray-600 mb-4">{module.description}</p>

                {module.id === 'identification' ? (
                  <div className="bg-gray-50 rounded-lg p-3 mb-4">
                    <div className="flex justify-between">
                      <div className="flex-1">
                        <p className="text-xs text-gray-500">Électeurs</p>
                        <p className="text-lg font-semibold text-gray-800">{new Intl.NumberFormat('fr-FR').format(stats.totalElecteurs)}</p>
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-gray-500">Identifiés</p>
                        <p className="text-lg font-semibold text-gray-800">{new Intl.NumberFormat('fr-FR').format(stats.electeursAvecTelephone)}</p>
                      </div>
                    </div>
                  </div>
                ) : module.stats && (
                  <div className="bg-gray-50 rounded-lg p-3 mb-4">
                    <p className="text-xs text-gray-500">{module.stats.label}</p>
                    <p className="text-lg font-semibold text-gray-800">{module.stats.value}</p>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span className={`text-sm font-medium ${module.color}`}>
                    {module.status === 'active' ? 'Accéder au module' :
                     module.status === 'coming-soon' ? 'Disponible bientôt' :
                     'Module verrouillé'}
                  </span>
                  {module.status === 'active' && (
                    <ChevronRight className={`w-5 h-5 ${module.color}`} />
                  )}
                </div>
              </div>

              {/* Status Overlay for non-active modules */}
              {module.status !== 'active' && (
                <div className="absolute inset-0 bg-gray-900/10 backdrop-blur-[1px]" />
              )}
            </div>
          ))}
        </div>

      </div>

      {/* Notification for clicked module */}
      {selectedModule && selectedModule.status !== 'active' && (
        <div className="fixed bottom-4 right-4 bg-gray-800 text-white px-6 py-3 rounded-lg shadow-xl animate-fade-in">
          <p className="font-medium">
            {selectedModule.status === 'coming-soon'
              ? `Le module ${selectedModule.name} sera bientôt disponible`
              : `Le module ${selectedModule.name} est actuellement verrouillé`}
          </p>
        </div>
      )}

      {/* Modal de changement de mot de passe obligatoire */}
      <ChangePasswordModal
        isOpen={showPasswordModal}
        isFirstLogin={true}
        onSuccess={handlePasswordChangeSuccess}
      />
    </div>
  );
};

export default App;